import React, { useState } from "react";
import PropTypes from "prop-types";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import 'react-phone-number-input/style.css';
import ContactHelper from "../../../../entities/contact/contact-helper";
import "./add-contact-modal.scss";

const AddContactModal = ({ show, onHide, onContactAdded }) => {
    const [contactName, setContactName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Validation function for phone/email requirement
    const validateForm = () => {
        if (!contactName.trim()) {
            return { isValid: false, error: "Contact name is required." };
        }
        if (!phoneNumber && !email.trim()) {
            return { isValid: false, error: "Either phone number or email is required." };
        }
        // Email validation if provided
        if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            return { isValid: false, error: "Please enter a valid email address." };
        }
        return { isValid: true };
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        const validation = validateForm();
        if (!validation.isValid) {
            setError(validation.error);
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            // Check if contact exists first
            const checkPayload = {};
            if (email.trim()) checkPayload.email = email.trim();
            if (phoneNumber) checkPayload.phone = phoneNumber;
            const existsCheck = await ContactHelper.checkContactExists(checkPayload);
            if (existsCheck) {
                setError("A contact with this email or phone number already exists.");
                setIsLoading(false);
                return;
            }
            // Create contact
            await ContactHelper.createNewContact({
                name: contactName.trim(),
                phone: phoneNumber || undefined,
                email: email.trim() || undefined,
            });
            onContactAdded();
            onHide();
            // Reset form
            setContactName("");
            setPhoneNumber("");
            setEmail("");
        } catch (err) {
            if (err?.response?.status === 404) {
                // Contact doesn't exist, proceed with creation
                try {
                    await ContactHelper.createNewContact({
                        name: contactName.trim(),
                        phone: phoneNumber || undefined,
                        email: email.trim() || undefined,
                    });
                    onContactAdded();
                    onHide();
                    // Reset form
                    setContactName("");
                    setPhoneNumber("");
                    setEmail("");
                } catch (createErr) {
                    setError(createErr?.response?.data?.message || "Failed to create contact.");
                }
            } else {
                setError(err?.response?.data?.message || "Failed to validate contact.");
            }
        }
        setIsLoading(false);
    };

    const handleClose = () => {
        setContactName("");
        setPhoneNumber("");
        setEmail("");
        setError(null);
        onHide();
    };

    if (!show) return null;

    return (
        <div
            className={`custom-modal-overlay ${show ? "show" : ""}`}
            role="dialog"
            aria-labelledby="addContactModalTitle"
            aria-hidden={!show}
        >
            <div className="custom-modal-content">
                <div className="modal-header">
                    <h5 className="modal-title mt-0" id="addContactModalTitle">
                        Add New Contact
                    </h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={handleClose}
                        aria-label="Close"
                    ></button>
                </div>
                <div className="modal-body">
                    {/* Optionally add an illustration here if desired */}
                    <div className="modal-form-section">
                        {error && <div className="alert alert-danger mb-3">{error}</div>}
                        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                            <div className="form-group">
                                <label>Contact Name *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter contact name"
                                    value={contactName}
                                    onChange={(e) => setContactName(e.target.value)}
                                    disabled={isLoading}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone Number</label>
                                <div className="phone-input-container">
                                    <PhoneInputWithCountrySelect
                                        placeholder="Enter phone number"
                                        defaultCountry="GB"
                                        value={phoneNumber}
                                        onChange={setPhoneNumber}
                                        international={true}
                                        withCountryCallingCode={true}
                                        countryCallingCodeEditable={false}
                                        countrySelectProps={{ unicodeFlags: true }}
                                        disabled={isLoading}
                                    />
                                </div>
                                <small className="text-muted">
                                    Either phone number or email is required
                                </small>
                            </div>
                            <div className="form-group">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    placeholder="Enter email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={isLoading}
                                />
                                <small className="text-muted">
                                    Either phone number or email is required
                                </small>
                            </div>
                        </form>
                    </div>
                </div>
                <div className="modal-footer justify-content-end">
                    <button
                        type="button"
                        className="btn"
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        <span>Close</span>
                    </button>
                    <button
                        type="submit"
                        className="bouncy-button small"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        <span>{isLoading ? "Creating..." : "Create Contact"}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

AddContactModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onContactAdded: PropTypes.func.isRequired,
};

export default AddContactModal;